import type { Dispatch } from 'react';
import { hotelQuestionnaireActions } from './hotelQuestionnaireActions';
import {
  fetchHotelStepsService,
  fetchQuestionnaireService,
  fetchSourcesService,
} from '../services/hotelDataService';
import {
  saveAnswersService,
  saveHotelDetailsAnswerService,
} from '../services/hotelAnswersService';
import type {
  HotelQuestionnaireAction,
  HotelQuestionnaireState,
} from '../types/hotelQuestionnaireTypes';
import type { SaveAnswersResponse } from '../../types/api';
import submissionsService, {
  Submission,
} from '../../services/submissionsService';
import { apiUtils } from '../../hooks';
import { API_ENDPOINTS } from '../../services/apiEndpoints';

/**
 * Async action to fetch hotel steps
 */
export const fetchStepsAsync =
  (dispatch: Dispatch<HotelQuestionnaireAction>) =>
  async (hotelId: string, isFasttrack?: boolean): Promise<void> => {
    if (!hotelId) return;

    dispatch(hotelQuestionnaireActions.setStepsLoading('initial'));
    dispatch(hotelQuestionnaireActions.setStepsError(null));

    try {
      const stepsData = await fetchHotelStepsService(hotelId, isFasttrack);
      console.log('Steps data from api ::', stepsData);
      dispatch(hotelQuestionnaireActions.setStepsData(stepsData));

      // Auto-load current step questionnaire
      if (stepsData.currentStepKey) {
        if (stepsData.currentStepKey === 'sources') {
          await fetchSourcesAsync(dispatch)(hotelId, undefined, isFasttrack);
        } else {
          await fetchQuestionnaireAsync(dispatch)(
            hotelId,
            stepsData.currentStepKey,
            undefined,
            isFasttrack
          );
        }
      }
    } catch (err) {
      console.error('❌ Failed to fetch hotel steps:', err);
      dispatch(
        hotelQuestionnaireActions.setStepsError(
          err instanceof Error ? err.message : 'Failed to fetch hotel steps'
        )
      );
    } finally {
      dispatch(hotelQuestionnaireActions.setStepsLoading('idle'));
    }
  };

/**
 * Async action to refresh hotel steps after save (without full page loading)
 */
export const refreshStepsAsync =
  (dispatch: Dispatch<HotelQuestionnaireAction>) =>
  async (
    hotelId: string,
    currentStepKey?: string,
    persistStep: boolean = false,
    isFasttrack?: boolean
  ): Promise<void> => {
    if (!hotelId) return;

    dispatch(hotelQuestionnaireActions.setStepsLoading('refreshing'));
    dispatch(hotelQuestionnaireActions.setStepsError(null));

    try {
      const stepsData = await fetchHotelStepsService(hotelId, isFasttrack);
      //persist the current step key in this case:
      //if the questionnaire is incomplete and user tried to refetch the steps after save
      stepsData.currentStepKey =
        currentStepKey && persistStep
          ? currentStepKey
          : stepsData.currentStepKey;
      dispatch(hotelQuestionnaireActions.setStepsData(stepsData));

      // Auto-load current step questionnaire after refresh
      if (stepsData.currentStepKey && !persistStep) {
        if (stepsData.currentStepKey === 'sources') {
          await fetchSourcesAsync(dispatch)(hotelId, undefined, isFasttrack);
        } else {
          await fetchQuestionnaireAsync(dispatch)(
            hotelId,
            stepsData.currentStepKey,
            undefined,
            isFasttrack
          );
        }
      }
    } catch (err) {
      console.error('❌ Failed to refresh hotel steps:', err);
      dispatch(
        hotelQuestionnaireActions.setStepsError(
          err instanceof Error ? err.message : 'Failed to refresh hotel steps'
        )
      );
    } finally {
      dispatch(hotelQuestionnaireActions.setStepsLoading('idle'));
    }
  };

/**
 * Async action to fetch questionnaire
 */
export const fetchQuestionnaireAsync =
  (dispatch: Dispatch<HotelQuestionnaireAction>) =>
  async (
    hotelId: string,
    stepKey: string,
    submissionId?: string,
    isFasttrack?: boolean
  ): Promise<void> => {
    if (!hotelId || !stepKey) return;

    dispatch(hotelQuestionnaireActions.setQuestionnaireLoading(true));
    dispatch(hotelQuestionnaireActions.setQuestionnaireError(null));

    try {
      console.log('📡 Starting API call for questionnaire...', {
        hotelId,
        stepKey,
        submissionId,
        isFasttrack,
      });
      const { questionnaireData, initialAnswers } =
        await fetchQuestionnaireService(hotelId, stepKey, submissionId, isFasttrack);

      dispatch(
        hotelQuestionnaireActions.setQuestionnaireData(questionnaireData)
      );
      dispatch(hotelQuestionnaireActions.setAnswers(initialAnswers));
      // Clear draft answers when new questionnaire data is loaded
      dispatch(hotelQuestionnaireActions.clearDraftAnswers());

      // DON'T update exclusion states on initial load - the backend already sends
      // the correct exclusion states based on current answers. Only update when answers change.
      // dispatch(hotelQuestionnaireActions.updateQuestionExclusionStates());
    } catch (err) {
      console.error('❌ Failed to fetch questionnaire:', err);
      dispatch(
        hotelQuestionnaireActions.setQuestionnaireError(
          err instanceof Error ? err.message : 'Failed to fetch questionnaire'
        )
      );
    } finally {
      //reset loading to false
      dispatch(hotelQuestionnaireActions.setQuestionnaireLoading(false));
    }
  };

/**
 * Async action to save answers
 */
export const saveAnswersAsync =
  (
    dispatch: Dispatch<HotelQuestionnaireAction>,
    state: HotelQuestionnaireState,
    refreshSteps: () => Promise<void>,
    fetchQuestionnaire: (stepKey: string) => Promise<void>,
    updateQuestionUploadedFilenames: (
      questionId: string,
      uploadedFilenames: string[]
    ) => void
  ) =>
  async (isDraft = false): Promise<SaveAnswersResponse | null> => {
    if (!state.hotelId || !state.questionnaireData) return null;

    dispatch(hotelQuestionnaireActions.setStepsLoading('saving'));

    try {
      // Combine main answers with draft answers (draft takes precedence)
      const combinedAnswers = { ...state.answers, ...state.draftAnswers };

      const isFasttrack = state.questionnaireType === 'FASTTRACK';

      console.log('Saving answers:', {
        mainAnswers: state.answers,
        draftAnswers: state.draftAnswers,
        combinedAnswers,
        isDraft,
      });

      let response: SaveAnswersResponse;
      if (state.stepsData?.currentStepKey === 'hotel-location') {
        response = await saveHotelDetailsAnswerService(
          state.hotelId,
          combinedAnswers
        );
      } else {
        response = await saveAnswersService(
          state.hotelId,
          state.questionnaireData,
          combinedAnswers,
          isDraft,
          updateQuestionUploadedFilenames,
          isFasttrack
        );
      }

      // If save is successful, apply draft answers to main state and clear draft
      if (response?.success) {
        if (!isDraft) {
          console.log('Applying draft answers to main state');
          dispatch(hotelQuestionnaireActions.applyDraftAnswers());
        } else {
          // For draft saves, also clear the draft state after successful save
          console.log('Clearing draft answers after successful draft save');
          dispatch(hotelQuestionnaireActions.applyDraftAnswers());
          dispatch(hotelQuestionnaireActions.clearDraftAnswers());
        }

        // Clean up local state by removing answers for excluded questions
        console.log('🧹 Cleaning up local state after successful save');
        dispatch(hotelQuestionnaireActions.cleanupExcludedQuestionAnswers());
      }

      return response;
    } catch (err) {
      console.error('❌ Failed to save answers:', err);
      throw err;
    } finally {
      dispatch(hotelQuestionnaireActions.setStepsLoading('idle'));
    }
  };

/**
 * Async action to go to a specific step
 */
export const goToStepAsync =
  (dispatch: Dispatch<HotelQuestionnaireAction>) =>
  async (
    hotelId: string,
    stepKey: string,
    submissionId?: string,
    isFasttrack?: boolean
  ): Promise<void> => {
    dispatch(hotelQuestionnaireActions.updateCurrentStep(stepKey));
    stepKey === "sources"
      ? await fetchSourcesAsync(dispatch)(hotelId, submissionId, isFasttrack)
      : await fetchQuestionnaireAsync(dispatch)(hotelId, stepKey, submissionId, isFasttrack);
  };

/**
 * Async action to fetch sources data
 */
export const fetchSourcesAsync =
  (dispatch: Dispatch<HotelQuestionnaireAction>) =>
  async (hotelId: string, submissionId?: string, isFasttrack?: boolean): Promise<void> => {
    if (!hotelId) return;

    dispatch(hotelQuestionnaireActions.setSourcesLoading(true));
    dispatch(hotelQuestionnaireActions.setSourcesError(null));

    try {
      const sourcesData = await fetchSourcesService(hotelId, submissionId, isFasttrack);
      dispatch(hotelQuestionnaireActions.setSourcesData(sourcesData));

      // Initialize reviews from backend data
      // Backend returns reviews as: { questionId: { approved, reason_id } }
      if (sourcesData?.reviews) {
        // console.log(
        //   'Initializing reviews from backend data',
        //   sourcesData.reviews
        // );
        const initialReviews: Record<string, any> = {};
        const backendReviews = sourcesData.reviews as Record<string, any>;

        // Iterate over the reviews object from backend
        Object.keys(backendReviews).forEach((questionId: string) => {
          const backendReview = backendReviews[questionId];
          initialReviews[questionId] = {
            questionId: questionId,
            approved: backendReview.approved,
            reasonId: backendReview.reason_id,
          };
        });

        // console.log('Initialized reviews from backend:', initialReviews);
        dispatch(hotelQuestionnaireActions.setReviews(initialReviews));
        // Clear any draft reviews when loading new sources
        dispatch(hotelQuestionnaireActions.clearDraftReviews());
      }
    } catch (err) {
      console.error('❌ Failed to fetch sources:', err);
      dispatch(
        hotelQuestionnaireActions.setSourcesError(
          err instanceof Error ? err.message : 'Failed to fetch sources'
        )
      );
    } finally {
      dispatch(hotelQuestionnaireActions.setSourcesLoading(false));
    }
  };

/**
 * Async action to submit questionnaire
 */
export const submitQuestionnaireAsync =
  (
    dispatch: Dispatch<HotelQuestionnaireAction>,
    goToStep: (stepKey: string) => Promise<void>
  ) =>
  async (hotelId: string, isFasttrack?: boolean): Promise<void> => {
    if (!hotelId) return;
    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.hotels.submitQuestionnaire(hotelId, isFasttrack)
      );
      console.log('Response on Saving Questionnaire ::', response.data);

      const responseData = response.data as any;

      // Check if the response indicates a failed submission with incomplete form
      if (responseData && responseData.status === 'failure') {
        // Throw error first to show toast message
        const errorMessage = responseData.message || 'Form submission failed';

        // Navigate to the incomplete step after a delay to let toast show first
        if (responseData.stepKey) {
          setTimeout(async () => {
            try {
              await goToStep(responseData.stepKey);
            } catch (err) {
              console.error('Failed to navigate to step:', err);
            }
          }, 1000); // 1.5 second delay to let toast show
        }

        throw new Error(errorMessage);
      }

      // Check for success status
      if (responseData && responseData.status === 'success') {
        // Success case - no error thrown, will show success toast
        return;
      }

      // Fallback: if no clear status, assume success
      return;
    } catch (error: any) {
      console.error('Failed to submit Questionnaire:', error);

      // Check if the error response contains the incomplete form data (fallback for old behavior)
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        // Check if this is an incomplete form error
        if (errorData.data && errorData.data.is_completed === false) {
          throw new Error(errorData.message || 'Form is incomplete');
        }

        // Check if the error response has a message
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      }

      // Re-throw the original error if it doesn't match our expected structure
      throw error;
    }
  };

/**
 * Async action to fetch readonly questionnaire (full detailed assessment)
 */
export const fetchReadonlyQuestionnaireAsync =
  (dispatch: Dispatch<HotelQuestionnaireAction>) =>
  async (hotelId: string): Promise<void> => {
    if (!hotelId) return;

    dispatch(hotelQuestionnaireActions.setReadonlyQuestionnaireLoading(true));
    dispatch(hotelQuestionnaireActions.setReadonlyQuestionnaireError(null));

    try {
      const { questionnaireData } = await fetchQuestionnaireService(
        hotelId,
        'full-questionnaire-readOnly'
      );
      dispatch(
        hotelQuestionnaireActions.setReadonlyQuestionnaireData(
          questionnaireData
        )
      );
    } catch (err) {
      console.error('❌ Failed to fetch readonly questionnaire:', err);
      dispatch(
        hotelQuestionnaireActions.setReadonlyQuestionnaireError(
          err instanceof Error
            ? err.message
            : 'Failed to fetch readonly questionnaire'
        )
      );
    } finally {
      dispatch(
        hotelQuestionnaireActions.setReadonlyQuestionnaireLoading(false)
      );
    }
  };

/**
 * Async action to fetch submissions
 */
export const fetchSubmissionsAsync =
  (dispatch: Dispatch<HotelQuestionnaireAction>) =>
  async (hotelId: string, isFasttrack?: boolean): Promise<void> => {
    if (!hotelId) return;

    dispatch(hotelQuestionnaireActions.setSubmissionsLoading(true));
    dispatch(hotelQuestionnaireActions.setSubmissionsError(null));

    try {
      console.log('📡 Fetching submissions for hotel:', hotelId);
      const response = await submissionsService.getSubmissions(hotelId, isFasttrack);

      if (response) {
        dispatch(
          hotelQuestionnaireActions.setSubmissions(response.data.submissions)
        );

        // // Set the first submission as selected by default if available
        // if (response.data.submissions.length > 0) {
        //   dispatch(
        //     hotelQuestionnaireActions.setSelectedSubmissionId(
        //       response.data.submissions.filter((sub: Submission) => sub.isLatest)?.[0]?.submissionId || null
        //     )
        //   );
        // }
      }
    } catch (err) {
      console.error('❌ Failed to fetch submissions:', err);
      dispatch(
        hotelQuestionnaireActions.setSubmissionsError(
          err instanceof Error ? err.message : 'Failed to fetch submissions'
        )
      );
    } finally {
      dispatch(hotelQuestionnaireActions.setSubmissionsLoading(false));
    }
  };
